import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Treatment } from '../../../types/admin';
import { DatePicker } from '../../../components/DatePicker';
import { Button } from '../../../components/ui';
import { PHOTO_ANGLE_OPTIONS } from '../../../api/photo-control';
import {
  usePhotoControlSchedules,
  useCreatePhotoControlSchedule,
  useDeletePhotoControlSchedule,
} from '../hooks/usePhotoControl';
import styles from './PhotoControlSchedulePanel.module.scss';

type PhotoControlSchedulePanelProps = {
  readonly patientId: string;
  readonly treatments: readonly Treatment[];
};

const INTERVAL_OPTIONS = [
  { value: 7, label: 'Кожні 7 днів' },
  { value: 10, label: 'Кожні 10 днів' },
  { value: 14, label: 'Кожні 14 днів' },
  { value: 21, label: 'Кожні 21 день' },
  { value: 30, label: 'Кожні 30 днів' },
];

type EndMode = 'date' | 'repetitions';

const ANGLE_LABEL_BY_VALUE = new Map(
  PHOTO_ANGLE_OPTIONS.map((opt) => [opt.value, opt.label]),
);

function describeRequiredAngles(
  requiredAngles: readonly string[] | null,
): string {
  if (requiredAngles === null || requiredAngles.length === 0) {
    return 'Усі 8 фото';
  }
  return requiredAngles
    .map((value) => ANGLE_LABEL_BY_VALUE.get(value) ?? value)
    .join(', ');
}

function computeEndDate(
  startDate: string,
  intervalDays: number,
  repetitions: number,
): string {
  const [y, m, d] = startDate.split('-').map(Number);
  if (!y || !m || !d || repetitions < 1) return '';
  const end = new Date(
    Date.UTC(y, m - 1, d + intervalDays * (repetitions - 1)),
  );
  if (Number.isNaN(end.getTime())) return '';
  return end.toISOString().slice(0, 10);
}

function formatDate(value: string, locale: string): string {
  return new Date(`${value}T00:00:00`).toLocaleDateString(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export function PhotoControlSchedulePanel({
  patientId,
  treatments,
}: PhotoControlSchedulePanelProps) {
  const { t, i18n } = useTranslation();
  const [selectedTreatmentId, setSelectedTreatmentId] = useState(
    treatments[0]?.id ?? '',
  );
  const [intervalDays, setIntervalDays] = useState(14);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [endMode, setEndMode] = useState<EndMode>('date');
  const [repetitions, setRepetitions] = useState(10);
  const [selectedAngles, setSelectedAngles] = useState<readonly string[]>(
    PHOTO_ANGLE_OPTIONS.map((opt) => opt.value),
  );
  const [requiresVideo, setRequiresVideo] = useState(true);
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  function toggleAngle(value: string) {
    setSelectedAngles((prev) =>
      prev.includes(value)
        ? prev.filter((v) => v !== value)
        : PHOTO_ANGLE_OPTIONS.filter(
            (opt) => opt.value === value || prev.includes(opt.value),
          ).map((opt) => opt.value),
    );
  }

  const schedulesQuery = usePhotoControlSchedules(
    patientId,
    selectedTreatmentId,
  );
  const createMutation = useCreatePhotoControlSchedule(patientId);
  const deleteMutation = useDeletePhotoControlSchedule(patientId);

  function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedTreatmentId || !startDate || selectedAngles.length === 0)
      return;

    const resolvedEndDate =
      endMode === 'repetitions'
        ? computeEndDate(startDate, intervalDays, repetitions)
        : endDate;

    createMutation.mutate({
      treatment_id: selectedTreatmentId,
      interval_days: intervalDays,
      start_date: startDate,
      end_date: resolvedEndDate || null,
      required_angles: selectedAngles,
      requires_video: requiresVideo,
    });
  }

  const schedules = schedulesQuery.data ?? [];

  useEffect(() => {
    if (!schedulesQuery.isLoading && schedules.length === 0) {
      setIsCreateOpen(true);
    }
  }, [schedules.length, schedulesQuery.isLoading]);

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <div className={styles.headerCopy}>
          <h4 className={styles.title}>
            {t('clientDetail.photoControl.schedule.title')}
          </h4>
          <p className={styles.subtitle}>
            {t('clientDetail.photoControl.schedule.subtitle')}
          </p>
        </div>
        {!schedulesQuery.isLoading && (
          <span className={styles.countBadge}>{schedules.length}</span>
        )}
      </div>

      {treatments.length > 1 && (
        <label className={`${styles.field} ${styles.treatmentField}`}>
          <span>Лікування</span>
          <select
            value={selectedTreatmentId}
            onChange={(e) => setSelectedTreatmentId(e.target.value)}
          >
            {treatments.map((t) => (
              <option key={t.id} value={t.id}>
                {t.diagnosis_titles?.length
                  ? t.diagnosis_titles.join(', ')
                  : t.diagnosis_title || t.id}
              </option>
            ))}
          </select>
        </label>
      )}

      {schedulesQuery.isLoading ? (
        <div
          className={styles.loadingState}
          aria-label={t('clientDetail.monitoring.schedule.loading')}
        >
          <span />
          <span />
        </div>
      ) : schedulesQuery.isError ? (
        <p className={styles.errorState} role="alert">
          {t('clientDetail.monitoring.schedule.loadFailed')}
        </p>
      ) : schedules.length > 0 ? (
        <div className={styles.scheduleList}>
          {schedules.map((schedule) => (
            <div key={schedule.id} className={styles.scheduleItem}>
              <div className={styles.scheduleInfo}>
                <div className={styles.scheduleTopline}>
                  <strong>
                    {t('photoSchedule.everyNDays', {
                      days: schedule.interval_days,
                    })}
                  </strong>
                  <span
                    className={`${styles.badge} ${schedule.is_active ? styles.active : styles.inactive}`}
                  >
                    {t(
                      `clientDetail.monitoring.schedule.${schedule.is_active ? 'active' : 'inactive'}`,
                    )}
                  </span>
                </div>
                <span className={styles.dateRange}>
                  {formatDate(schedule.start_date, i18n.language)}
                  <span aria-hidden="true">→</span>
                  {schedule.end_date
                    ? formatDate(schedule.end_date, i18n.language)
                    : t('clientDetail.monitoring.schedule.noEndDate')}
                </span>
                <span className={styles.requirements}>
                  {describeRequiredAngles(schedule.required_angles)} &middot;{' '}
                  {schedule.requires_video ? '+ відео' : 'без відео'}
                </span>
              </div>
              <div className={styles.scheduleActions}>
                {schedule.is_active && (
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => deleteMutation.mutate(schedule.id)}
                    disabled={deleteMutation.isPending}
                  >
                    Деактивувати
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className={styles.empty}>Розклади не налаштовано</p>
      )}

      <details
        className={styles.createDisclosure}
        open={isCreateOpen}
        onToggle={(event) => setIsCreateOpen(event.currentTarget.open)}
      >
        <summary>
          <span className={styles.summaryIcon} aria-hidden="true">
            +
          </span>
          <span>
            <strong>{t('clientDetail.monitoring.schedule.newTitle')}</strong>
            <small>{t('clientDetail.photoControl.schedule.newSubtitle')}</small>
          </span>
        </summary>
        <div className={styles.createForm}>
          <form onSubmit={handleCreate}>
            <div className={styles.formGrid}>
              <label className={styles.field}>
                <span>{t('clientDetail.monitoring.schedule.interval')}</span>
                <select
                  value={intervalDays}
                  onChange={(e) => setIntervalDays(Number(e.target.value))}
                >
                  {INTERVAL_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className={styles.field}>
                <span>{t('clientDetail.monitoring.schedule.startDate')}</span>
                <DatePicker
                  value={startDate}
                  onChange={setStartDate}
                  placeholder={t(
                    'clientDetail.monitoring.schedule.datePlaceholder',
                  )}
                  required
                />
              </label>
              <label className={styles.field}>
                <span>{t('clientDetail.monitoring.schedule.endMode')}</span>
                <select
                  value={endMode}
                  onChange={(e) => setEndMode(e.target.value as EndMode)}
                >
                  <option value="date">
                    {t('clientDetail.monitoring.schedule.endByDate')}
                  </option>
                  <option value="repetitions">
                    {t('clientDetail.monitoring.schedule.endByRepetitions')}
                  </option>
                </select>
              </label>
              {endMode === 'date' ? (
                <label className={styles.field}>
                  <span>
                    {t('clientDetail.monitoring.schedule.endDateOptional')}
                  </span>
                  <DatePicker
                    value={endDate}
                    onChange={setEndDate}
                    placeholder={t(
                      'clientDetail.monitoring.schedule.datePlaceholder',
                    )}
                  />
                </label>
              ) : (
                <label className={styles.field}>
                  <span>
                    {t('clientDetail.monitoring.schedule.repetitions')}
                  </span>
                  <input
                    type="number"
                    min={1}
                    max={365}
                    value={repetitions}
                    onChange={(e) =>
                      setRepetitions(Math.max(1, Number(e.target.value) || 1))
                    }
                  />
                </label>
              )}
            </div>

            <fieldset className={styles.checkboxGroup}>
              <legend className={styles.checkboxGroupLabel}>
                {t('clientDetail.photoControl.schedule.requiredPhotos')}
              </legend>
              <div className={styles.checkboxOptions}>
                {PHOTO_ANGLE_OPTIONS.map((opt) => (
                  <label key={opt.value} className={styles.checkboxOption}>
                    <input
                      type="checkbox"
                      checked={selectedAngles.includes(opt.value)}
                      onChange={() => toggleAngle(opt.value)}
                    />
                    <span>{opt.label}</span>
                  </label>
                ))}
              </div>
              {selectedAngles.length === 0 && (
                <span className={styles.checkboxError}>
                  {t('clientDetail.photoControl.schedule.selectPhoto')}
                </span>
              )}
            </fieldset>

            <label className={`${styles.checkboxOption} ${styles.videoOption}`}>
              <input
                type="checkbox"
                checked={requiresVideo}
                onChange={(e) => setRequiresVideo(e.target.checked)}
              />
              <span>
                {t('clientDetail.photoControl.schedule.requireVideo')}
              </span>
            </label>

            <Button
              type="submit"
              variant="primary"
              size="md"
              disabled={
                createMutation.isPending ||
                !startDate ||
                selectedAngles.length === 0
              }
            >
              {createMutation.isPending
                ? t('clientDetail.monitoring.schedule.creating')
                : t('clientDetail.monitoring.schedule.create')}
            </Button>
            {createMutation.isError && (
              <p className={styles.errorState} role="alert">
                {t('clientDetail.monitoring.schedule.createFailed')}
              </p>
            )}
          </form>
        </div>
      </details>
    </div>
  );
}
